import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useCallback,
  useEffect
} from 'react';
import { useLocation } from 'react-router-dom';
import { routePathMap } from 'modules/common';

export type NotesToastContext = {
  showSuccess?: boolean;
  showError?: boolean;
  showMessage?: boolean;
  message: string;
  values?: Record<string, string>;
};

type NotesToastContextType = {
  toastContext: NotesToastContext;
  setToastContext: (input: NotesToastContext) => void;
  dismiss: boolean;
};

const getDefaultToastContext = (): NotesToastContext => {
  return {
    showMessage: false,
    showSuccess: false,
    showError: false,
    message: 'toast.note.added'
  };
};

export const NotesToastContext = createContext<NotesToastContextType>({
  toastContext: getDefaultToastContext(),
  setToastContext: () => {},
  dismiss: false
});

export const useNotesToastContext = (): NotesToastContextType =>
  useContext(NotesToastContext);

const NotesToastContextProvider: React.FC = ({
  children
}: {
  children?: React.ReactNode;
}) => {
  const [toastContext, setToastContextState] = useState(
    getDefaultToastContext()
  );

  const [dismiss, setDimissState] = useState(false);

  const location = useLocation();
  useEffect(() => {
    setDimissState(location.pathname === routePathMap.smartDiary);
  }, [location.pathname]);

  const setToastContext = useCallback(
    ({
      showMessage = false,
      showSuccess = false,
      showError = false,
      message,
      values
    }: NotesToastContext) => {
      setToastContextState({
        showMessage,
        showSuccess,
        showError,
        message,
        values
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      toastContext,
      setToastContext,
      dismiss
    }),
    [toastContext, setToastContext, dismiss]
  );

  return (
    <NotesToastContext.Provider value={value}>
      {children}
    </NotesToastContext.Provider>
  );
};

export default NotesToastContextProvider;
